import React, { useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { ExternalLink, Github, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const techBadgeStyle = (tech) => {
  const styles = {
    'Next.js': 'bg-slate-700 text-slate-200',
    React: 'bg-cyan-900/50 text-cyan-300',
    TypeScript: 'bg-blue-900/40 text-blue-300',
    Python: 'bg-yellow-900/30 text-yellow-300',
    Django: 'bg-green-900/30 text-green-300',
    PostgreSQL: 'bg-sky-900/40 text-sky-300',
    'Tailwind CSS': 'bg-teal-900/30 text-teal-300',
    GeminiAi: 'bg-violet-900/40 text-violet-300',
    'NextAuth.js': 'bg-slate-600/60 text-slate-300',
    Stripe: 'bg-purple-900/40 text-purple-300',
    JWT: 'bg-orange-900/30 text-orange-300',
    emailjs: 'bg-red-900/30 text-red-300',
    'Moment.js': 'bg-amber-900/30 text-amber-300',
  };
  return styles[tech] || 'bg-slate-700/70 text-slate-400';
};

const TiltCard = ({ project, index }) => {
  const ref = useRef(null);
  const cardRef = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const rotateX = useTransform(mouseY, [-120, 120], [8, -8]);
  const rotateY = useTransform(mouseX, [-120, 120], [-8, 8]);
  const glowX = useTransform(mouseX, [-120, 120], [0, 100]);
  const glowY = useTransform(mouseY, [-120, 120], [0, 100]);

  const springRotateX = useSpring(rotateX, { stiffness: 150, damping: 20 });
  const springRotateY = useSpring(rotateY, { stiffness: 150, damping: 20 });

  const handleMouseMove = (e) => {
    const rect = cardRef.current.getBoundingClientRect();
    mouseX.set(e.clientX - (rect.left + rect.width / 2));
    mouseY.set(e.clientY - (rect.top + rect.height / 2));
  };

  const handleMouseLeave = () => {
    mouseX.set(0);
    mouseY.set(0);
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, delay: index * 0.07, ease: 'easeOut' }}
      style={{ perspective: '1000px' }}
    >
      <motion.div
        ref={cardRef}
        style={{
          rotateX: springRotateX,
          rotateY: springRotateY,
          transformStyle: 'preserve-3d',
        }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        className="group relative bg-slate-800 border border-slate-700 rounded-xl p-6 hover:border-violet-500/40 transition-colors duration-300 h-full"
      >
        {/* Dynamic spotlight glow */}
        <motion.div
          className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: useTransform(
              [glowX, glowY],
              ([x, y]) =>
                `radial-gradient(circle at ${x}% ${y}%, rgba(124,58,237,0.08) 0%, transparent 60%)`
            ),
          }}
        />

        {/* Top gradient line */}
        <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-violet-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        <div className="relative" style={{ transform: 'translateZ(20px)' }}>
          <div className="flex justify-between items-start mb-4 gap-3">
            <h3 className="text-base font-bold text-slate-100 group-hover:text-white transition-colors duration-200 leading-snug">
              {project.title}
            </h3>
            <div className="flex gap-3 flex-shrink-0 mt-0.5">
              <a
                href={project.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-slate-200 transition-colors duration-200"
                title="GitHub"
              >
                <Github className="w-4 h-4" />
              </a>
              <a
                href={project.demoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-500 hover:text-violet-400 transition-colors duration-200"
                title="Live demo"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>

          <p className="text-slate-400 text-sm leading-relaxed mb-5">
            {project.description}
          </p>

          <div className="flex flex-wrap gap-1.5">
            {project.technologies.map((tech, i) => (
              <span
                key={i}
                className={`text-xs font-medium px-2.5 py-1 rounded-md ${techBadgeStyle(tech)}`}
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Projects = () => {
  const titleRef = useRef(null);
  const titleInView = useInView(titleRef, { once: true });

  const projects = [
    {
      title: 'AllInBio',
      technologies: ['Next.js', 'React', 'Tailwind CSS', 'PostgreSQL', 'NextAuth.js', 'Stripe'],
      description:
        'A modern link-in-bio platform that allows creators and influencers to build customizable landing pages with analytics tracking, OAuth authentication, subscription management, and mobile optimization.',
      githubLink: 'https://github.com/KajetanJedrych/bio-links',
      demoLink: 'https://allinbio.page',
    },
    {
      title: 'SummaryAI',
      technologies: ['React', 'TypeScript', 'Next.js', 'Tailwind CSS', 'GeminiAi'],
      description:
        'A website that exports and analyzes YouTube comments using Gemini to generate summaries and insights from video discussions.',
      githubLink: 'https://github.com/KajetanJedrych/summaryai',
      demoLink: 'https://www.jedrychk.com/blog/2',
    },
    {
      title: 'Calculator Page',
      technologies: ['React', 'TypeScript', 'Next.js', 'Tailwind CSS'],
      description:
        'A website for calculating Polish taxes, social security, and investment benefits (IKE, IKZE) for individuals and businesses.',
      githubLink: 'https://github.com/KajetanJedrych/calc',
      demoLink: 'https://kalkulatory.org/',
    },
    {
      title: 'Business Card Site',
      technologies: ['React', 'Tailwind CSS'],
      description:
        'Business card site created for a local service provider in the window repair industry.',
      githubLink: 'https://github.com/KajetanJedrych/profenst',
      demoLink: 'https://kajetanjedrych.github.io/profenst/',
    },
    {
      title: 'Massage Studio Backend',
      technologies: ['Django', 'JWT', 'PostgreSQL'],
      description:
        'Backend of a calendar application for a massage studio, developed as part of a university course to explore modern backend technologies.',
      githubLink: 'https://github.com/KajetanJedrych/DjangoWSB',
      demoLink: 'https://github.com/KajetanJedrych/DjangoWSB',
    },
    {
      title: 'Portfolio Website',
      technologies: ['React', 'Tailwind CSS', 'emailjs'],
      description:
        'Responsive personal portfolio showcasing skills, experience, and projects with modern, interactive design.',
      githubLink: 'https://github.com/KajetanJedrych/portfolioPage',
      demoLink: 'https://www.jedrychk.com/',
    },
    {
      title: 'Massage Studio Frontend',
      technologies: ['React', 'TypeScript', 'Moment.js'],
      description:
        'Frontend of a calendar application for a massage studio, exploring modern web technologies and UI patterns.',
      githubLink: 'https://github.com/KajetanJedrych/CalendarAppFE',
      demoLink: 'https://github.com/KajetanJedrych/CalendarAppFE',
    },
    {
      title: 'Route Tracker',
      technologies: ['Next.js', 'TypeScript', 'Google Maps API', 'Node.js', 'nginx'],
      description:
        'A commute intelligence dashboard that collects Google Maps travel time data every 10 minutes on weekdays and visualizes trends to analyze how long the route to work takes.',
      githubLink: 'https://github.com/KajetanJedrych/route-tracker',
      demoLink: 'http://doskc4osowg4oggoogk088wc.195.201.2.123.sslip.io/',
    },
  ];

  return (
    <>
      <SEO
        title="Projekty"
        description="Przegląd projektów Kajetana Jędrycha — aplikacje webowe, strony internetowe i rozwiązania AI zbudowane w React, Next.js, Python i innych technologiach."
        path="/projects"
      />
      <section className="bg-slate-900 py-24 px-6 min-h-screen" id="projects">
        <div className="max-w-6xl mx-auto">
          <motion.div
            ref={titleRef}
            initial={{ opacity: 0, y: 20 }}
            animate={titleInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-violet-400 text-xs font-semibold tracking-[0.2em] uppercase mb-3">
              Portfolio
            </p>
            <h2 className="text-4xl font-bold text-slate-100 mb-4">My Projects</h2>
            <div className="w-16 h-px bg-gradient-to-r from-violet-500 to-cyan-400 mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map((project, index) => (
              <TiltCard key={index} project={project} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA section */}
      <section className="bg-slate-900 border-t border-slate-800 py-20 px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-slate-100 mb-4">Have a Project in Mind?</h2>
            <p className="text-slate-400 mb-8 leading-relaxed">
              I'm always excited to collaborate on innovative projects. Whether you have a specific
              idea or need technical expertise, I'm ready to bring your vision to life.
            </p>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-violet-600 hover:bg-violet-500 text-white px-8 py-4 rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:shadow-violet-500/25 hover:scale-105 active:scale-95"
            >
              <MessageCircle className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300" />
              Contact Me
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Projects;
